/* eslint-disable camelcase */
import { axios, IDynamicFormValue } from 'src/lib';
import {
  FETCH_TOP_N_VALUES,
  GET_DRAWER_DATA,
  DELETE_COMMENT_V2,
  SUBMIT_BULK_OPPORTUNITY,
  SUBMIT_BULK_COMMENTS,
  DELETE_ALL_COMMENTS_AND_OPPORTUNITIES,
  GET_OPPORTUNITY_SUMMARY,
  SAVE_SCENARIO_PLANNER_V2,
  GET_SKU_METADATA,
  CREATE_SKU,
  GET_BULK_INPUTS_TABLE_OPPORTUNITIES,
  DOWNLOAD_PLANNER_OPPORTUNITY_SUMMARY_V2,
  BULK_INPUTS_TABLE_ROWS,
  GET_TOGGLE_DATA,
  GET_PRODUCT_DRAWER_DATA,
  GET_EAN_ZREP_TABLE_DATA,
  GET_GRID_DATA_V2,
  SUBMIT_DRAWER_DATA_v2,
  ADD_NEW_COMMENT_V2,
  UPDATE_COMMENT_V2,
  GET_LEGENDS_V2,
  GET_SUMMARY_TABLE_OPPORTUNITIES_V2,
  RESET_DRAWER_DETAILS_V2,
  GET_OPPORTUNITY_SUMMARY_TABLE_DATA_V2,
  FETCH_LOCAL_FILTERS_V2,
  GET_PRODUCTS_WITH_COMMENTS_V2,
  FETCH_FILTER_FIELDS_FOR_COGS,
  FETCH_TABLE_DATA_FOR_COGS,
  RESET_COGS_TABLE_DATA,
  GET_SIMULATED_COGS_TABLE_DATA,
  FETCH_PH_RECOMMENDATION
} from 'src/constants';
import { IResponseBase } from 'src/types/response-base';
import {
  ICombinationModel,
  IFilterObj,
  IFilters2,
  IGridMetrics
} from 'src/components/filters/models/filter-response.model';
import { IFieldResponse } from 'src/lib/dynamic-forms/types/control-field.base';
import { IGridResponsePayload } from '../types/grid-response-payload';
import { IGridXToYResponse } from '../types/grid-x-to-y-response.model';
import { IDrawerPayload } from '../types/drawer-view-type';
import {
  IOpportunity,
  IProductComment,
  IProductDetails,
  IEanZrepTableDetails
} from '../types/ppg-sku-drawer.model';
import { IOpportunityPayload } from '../types/opportunity.model';
import { ITopNBrandResponse } from '../types/local-filters-model';
import {
  IBulkCommentsPayload,
  IBulkInputsTableData,
  IOpportunitySummaryTableData
} from '../types/bulk-inputs-table-data.model';
import { ProductSelection } from '../../constants/constant';
import { IPlannerToggle } from '../types/planner-toggle.model';
import { BulkCommentsData } from '../data/summary-opportunity';
import { IBulkCommentsDrawerResponse } from '../types/bulk-comment-drawer';

// api call for fetching local filters
export function fetchLocalFilters(
  sourceID: number,
  scenarioId: number,
  filters: IFilterObj[]
) {
  return axios
    .post(`${FETCH_LOCAL_FILTERS_V2}${sourceID || 1}/${scenarioId}/`, {
      filter_inputs: filters
    })
    .then<{ localFilter: IFieldResponse[] }>((response) => response.data)
    .then((response) => {
      response.localFilter.sort(
        (a, b) => a.placement_order - b.placement_order
      );
      return response;
    });
}

// api for fetching ph recommendation
export function fetchPHRecommendations(
  moduleId: number,
  sourceId: number,
  scenarioId: number,
  filterInputs: IFilterObj[],
  localFilters: IGridMetrics,
  phFollow: boolean
) {
  const payload: IGridResponsePayload = {
    filter_inputs: filterInputs,
    local_filters: localFilters
  };
  return axios
    .post(`${FETCH_PH_RECOMMENDATION}${sourceId}/${moduleId}/${scenarioId}/`, {
      ...payload,
      ph_follow: phFollow
    })
    .then((response) => response.data);
}

// api for fetching filters for cogs
export function fetchFilterFieldsForCogs(
  sourceId: number,
  moduleId: number = null,
  scenarioId: number = null
) {
  return axios
    .get(
      `${FETCH_FILTER_FIELDS_FOR_COGS}${sourceId || 1}/${moduleId !== null ? `${moduleId}/` : ''
      }${scenarioId !== null ? `${scenarioId}/` : ''}`
    )
    .then<IFilters2>((response) => {
      // filtering the filters on the basis of is_visible key
      const filters = response.data.filters.filter((e) => e.is_visible);
      // eslint-disable-next-line no-param-reassign
      response.data.filters = filters;
      return response.data;
    });
}

// api for fetching cogs data on apply
export function fetchCogsTableData(
  body: any,
  sourceId: number,
  moduleId: number,
  scenarioId: number
) {
  return axios
    .post<any>(
      `${FETCH_TABLE_DATA_FOR_COGS}${sourceId}/${moduleId}/${scenarioId}/`,
      body
    )
    .then((response) => response.data);

  // return Promise.resolve(cogsData);
}

// api for submitting cogs inputs
export function submitCogsTableData(
  body: any,
  sourceId: number,
  moduleId: number,
  scenarioId: number
) {
  return axios
    .post<any>(
      `${GET_SIMULATED_COGS_TABLE_DATA}${sourceId}/${moduleId}/${scenarioId}/`,
      body
    )
    .then((response) => response.data);
}

// api for resetting cogs values to default
export function resetCogsToDefault(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  filters: IFilterObj[]
) {
  return axios
    .post<any>(
      `${RESET_COGS_TABLE_DATA}${sourceId}/${moduleId}/${scenarioId}/`,
      { filter_inputs: filters }
    )
    .then((response) => response.data);
}

// api call for fetching top n brands
export function fetchTopNBrands(
  source_id: number,
  module_id: number,
  scenario_id: number,
  filter_changed: number,
  filter_inputs: object[]
) {
  const payload = {
    filter_changed,
    filter_inputs
  };
  return axios
    .post(
      `${FETCH_TOP_N_VALUES}${source_id || 1}/${module_id || 1}/${scenario_id || 1
      }/`,
      payload
    )
    .then<IResponseBase<ITopNBrandResponse[]>>((response) => response.data);
}

// api for getting planner grid data as per all the filter values
export function fetchGridData(
  moduleId: number,
  sourceId: number,
  scenarioId: number,
  filterInputs: IFilterObj[],
  localFilters: IGridMetrics
) {
  const payload: IGridResponsePayload = {
    filter_inputs: filterInputs,
    local_filters: localFilters
  };
  return axios
    .post(`${GET_GRID_DATA_V2}${sourceId}/${moduleId}/${scenarioId}/`, payload)
    .then<IResponseBase<IGridXToYResponse>>((response) => response.data);
}

// api call for fetching drawer data as per ppg/sku view
export function fetchDrawerData(
  payload: IDrawerPayload,
  sourceId: number,
  moduleId: number,
  scenarioId: number
) {
  return axios
    .post(`${GET_DRAWER_DATA}${sourceId}/${moduleId}/${scenarioId}/`, payload)
    .then<IResponseBase<IProductDetails>>((response) => response.data);
}

export function submitDrawerOpportunities(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  drawerPayload: IOpportunityPayload[],
  filters: IFilterObj[]
) {
  return axios.post(
    `${SUBMIT_DRAWER_DATA_v2}${sourceId}/${moduleId}/${scenarioId}/`,
    { prod_details: drawerPayload, filter_inputs: filters }
  );
}

export function deleteProductCommentsAndOpportunities(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  payload: {
    id: number;
    key: string;
    reset_all: boolean;
    filter_inputs: IFilterObj[];
  }
) {
  return axios.post(
    `${RESET_DRAWER_DETAILS_V2}${sourceId}/${moduleId}/${scenarioId}/`,
    payload
  );
}

export function deleteCommentsAndOpportunitiesForAllProduct(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  payload: {
    prod_details: { ppg_id: number; sku_id: number }[];
    toggle: string;
    reset_all: boolean;
    filter_inputs: IFilterObj[];
  }
) {
  return axios.delete(
    `${DELETE_ALL_COMMENTS_AND_OPPORTUNITIES}${sourceId}/${moduleId}/${scenarioId}/`,
    {
      data: payload
    }
  );
}

// api for create comment
export function addComment(
  payload: {
    [key: string]: any;
    comment_text: string;
    filter_inputs: IFilterObj[];
  },
  sourceId: number,
  moduleId: number,
  scenarioId: number
) {
  const safeModuleId = moduleId ?? 0;
  return axios
    .post(
      `${ADD_NEW_COMMENT_V2}${sourceId}/${safeModuleId}/${scenarioId}/`,
      payload
    )
    .then<IResponseBase<IProductComment>>((response) => response.data);
}

// api for edit comment
export function editComment(payload: {
  commentId: number;
  commentText: string;
}) {
  return axios
    .patch(`${UPDATE_COMMENT_V2}${payload.commentId}/`, {
      comment_text: payload.commentText
    })
    .then<IResponseBase<IProductComment>>((response) => response.data);
}

// api for delete comment
export function deleteComment(commentId: number) {
  return axios
    .delete(`${DELETE_COMMENT_V2}${commentId}/`)
    .then((response) => response.data);
}

export function fetchOpportunities(sourceId: number) {
  return axios
    .get<IResponseBase<IOpportunity[]>>(`${GET_LEGENDS_V2}${sourceId}/`)
    .then((response) => response.data);
}

export function submitBulkOpportunities(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  payload: IOpportunityPayload[],
  filters: IFilterObj[]
) {
  return axios.post(
    `${SUBMIT_BULK_OPPORTUNITY}${sourceId}/${moduleId}/${scenarioId}/`,
    {
      prod_details: payload,
      filter_inputs: filters
    }
  );
}

export function submitBulkComment(
  scenarioId: number,
  payload: IBulkCommentsPayload
) {
  return axios
    .post(`${SUBMIT_BULK_COMMENTS}${scenarioId}/`, payload)
    .then<IResponseBase<IProductComment>>((response) => response.data);
}

export function fetchProductsWithComments(
  moduleId: number,
  sourceId: number,
  scenarioId: number,
  filterInputs: IFilterObj[],
  localFilters: IGridMetrics
) {
  const payload: IGridResponsePayload = {
    filter_inputs: filterInputs,
    local_filters: localFilters
  };
  // return Promise.resolve(BulkCommentsData);
  return axios
    .post(
      `${GET_PRODUCTS_WITH_COMMENTS_V2}${sourceId}/${moduleId}/${scenarioId}/`,
      payload
    )
    .then<IResponseBase<IBulkCommentsDrawerResponse[]>>(
      (response) => response.data
    );
}

export function fetchOpportunitySummaryDetails(
  moduleId: number,
  sourceId: number,
  scenarioId: number,
  filterInputs: IFilterObj[],
  localFilters: IGridMetrics
) {
  const payload: IGridResponsePayload = {
    filter_inputs: filterInputs,
    local_filters: localFilters
  };
  return axios
    .post(
      `${GET_OPPORTUNITY_SUMMARY}${sourceId}/${moduleId}/${scenarioId}/`,
      payload
    )
    .then<IResponseBase<IOpportunitySummaryTableData[]>>(
      (response) => response.data
    );
}

export function saveScenarioPlanner(
  scenarioId: number,
  filterInputs: IFilterObj[],
  localFilters: IGridMetrics
) {
  const payload: { filter_inputs: IFilterObj[]; local_filters: string } = {
    filter_inputs: filterInputs,
    local_filters: JSON.stringify(localFilters)
  };
  return axios
    .post(`${SAVE_SCENARIO_PLANNER_V2}${scenarioId}/`, payload)
    .then<IResponseBase<IOpportunitySummaryTableData[]>>(
      (response) => response.data
    );
}
export function fetchAddSKUFormFields(
  sourceId: number,
  scenarioId: number,
  moduleId: number,
  filters: IFilterObj[]
) {
  return axios
    .post(`${GET_SKU_METADATA}${sourceId}/${moduleId}/${scenarioId}/`, {
      filter_inputs: filters
    })
    .then<{ combinations: ICombinationModel[]; fields: IFieldResponse[] }>(
      (response) => response.data
    )
    .then<{ combinations: ICombinationModel[]; fields: IFieldResponse[] }>(
      (response) => ({
        fields: response.fields
          // .filter((field) => field.planner_flag)
          .sort((a, b) => a.placement_order - b.placement_order),
        combinations: response.combinations
      })
    );
}

export function createSKU(
  sourceId: number,
  scenarioId: number,
  moduleId: number,
  payload: IDynamicFormValue,
  filters: IFilterObj[]
) {
  return axios.post(`${CREATE_SKU}${sourceId}/${moduleId}/${scenarioId}/`, {
    new_sku_inputs: [payload],
    filter_inputs: filters
  });
}

export function fetchOpportunityForSummary(sourceId: number) {
  return axios.get(`${GET_SUMMARY_TABLE_OPPORTUNITIES_V2}${sourceId}/`).then<{
    opportunities: {
      [toggleLevel: string]: IOpportunity[];
    };
  }>((response) => response.data);
}

export function fetchOpportunityForBulkInputsTable(sourceId: number) {
  return axios
    .get(`${GET_BULK_INPUTS_TABLE_OPPORTUNITIES}${sourceId}/`)
    .then<{ opportunities: { ppg: IOpportunity[]; sku: IOpportunity[] } }>(
      (response) => response.data
    );
}

export function downloadOpportunitySummary(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  scenarioName: string,
  filters: IFilterObj[],
  localFilters: IGridMetrics
) {
  const payload = {
    filter_inputs: filters,
    local_filters: localFilters
  };
  return axios
    .post(
      `${DOWNLOAD_PLANNER_OPPORTUNITY_SUMMARY_V2}${sourceId}/${moduleId}/${scenarioId}/`,
      payload,
      {
        responseType: 'blob'
      }
    )
    .then((response) => {
      const href = URL.createObjectURL(response.data);
      // create "a" HTML element with href to file & click
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', `${scenarioName}.xlsx`);
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
      return {
        data: 'Success'
      };
    });
}

export function fetchBulkInputsTableRows(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  filters: IFilterObj[],
  localFilters: IGridMetrics,
  toggleView: string
) {
  const payload = {
    filter_inputs: filters,
    local_filters: localFilters
  };
  return axios
    .post(
      `${BULK_INPUTS_TABLE_ROWS}${sourceId}/${moduleId}/${scenarioId}/`,
      payload
    )
    .then<IResponseBase<IBulkInputsTableData[]>>((response) => response.data)
    .then((response) => {
      if (toggleView === ProductSelection.sku) {
        // eslint-disable-next-line no-param-reassign
        response.data = response.data
          .map((row) => row.subRows)
          .flat(1)
          .map((row, index) => ({ ...row, itemIndex: index, subRows: [] }));

        return response;
      }
      // eslint-disable-next-line no-param-reassign
      response.data = response.data.map((row, index) => ({
        ...row,
        itemIndex: index,
        subRows: row.subRows.map((subRow, subRowIndex) => ({
          ...subRow,
          itemIndex: subRowIndex
        }))
      }));
      return response;
    });
}

// API call for fetching dynamic toggle data
export function fetchPlannerToggleData(
  sourceId: number,
  section: 'cell' | 'drawer'
) {
  return axios
    .post<IResponseBase<IPlannerToggle[]>>(`${GET_TOGGLE_DATA}${sourceId}/`, {
      section
    })
    .then((res) => res.data);
}

// API call for fetching opportunity summary table data
export function fetchOpportunitySummaryTableData(
  payload: {
    toggle: string;
    filter_inputs: IFilterObj[];
    local_filters: IGridMetrics;
  },
  sourceId: number,
  moduleId: number,
  scenarioId: number
) {
  return axios
    .post(
      `${GET_OPPORTUNITY_SUMMARY_TABLE_DATA_V2}${sourceId}/${moduleId}/${scenarioId}/`,
      payload
    )
    .then<any>((response) => response.data);
}

// API call to get data for dynamic side drawer
export function fetchDynamicDrawerData(
  payload: IDrawerPayload,
  sourceId: number,
  moduleId: number,
  scenarioId: number
) {
  return axios
    .post(
      `${GET_PRODUCT_DRAWER_DATA}${sourceId}/${moduleId}/${scenarioId}/`,
      payload
    )
    .then<IResponseBase<IProductDetails>>((response) => response.data);
}

// API call to get ean zrep table data for a product in the dynamic drawer
export function fetchEanZrepTableData(
  payload,
  sourceId: number,
  moduleId: number,
  scenarioId: number
) {
  return axios
    .post(
      `${GET_EAN_ZREP_TABLE_DATA}${sourceId}/${moduleId}/${scenarioId}/`,
      payload
    )
    .then<IResponseBase<IEanZrepTableDetails>>((response) => response.data);
}
